import { Info, Note } from "@phosphor-icons/react"
import { Icon } from "@tokenterminal/core/ui/icon/Icon"
import { Card } from "@tokenterminal/core/ui/layout/card/Card"
import { useFeatureFlagEnabled } from "posthog-js/react"
import { useMemo, useEffect, useState } from "react"
import { twMerge } from "tailwind-merge"
import { getBackgroundStyles } from "./themes"
import { type ChartImageState } from "./types"

// Responsible for rendering a card with a chart-like UI inside. Includes background image, padding, and appropriate dimensions.

export const ChartImagePreviewWrapper = ({
  innerRef,
  state,
  children,
  sourcesAndDefinitionsText,
  footnoteText,
  padding = 60,
  dimensionsStyles,
}: {
  children: React.ReactNode
  innerRef: React.RefObject<HTMLDivElement>
  state: ChartImageState
  sourcesAndDefinitionsText: string
  footnoteText?: string
  padding?: number
  dimensionsStyles?: Record<string, string>
}) => {
  const isChartFootnotesEnabled = useFeatureFlagEnabled("chart_footnotes")
  const [mounted, setMounted] = useState(false)
  const { background, text } = getBackgroundStyles(state.background)

  // Charts internally have a resize observer that updates the chart size when
  // the window is resized. When the modal is opened, the chart can get stuck in
  // a state where it's not the correct size, I think due to the modal's initial
  // animation. This is a hacky fix to force the chart to resize once the modal
  // is done with it's initial animation.
  useEffect(() => {
    setTimeout(() => {
      setMounted(true)
    }, 200)
  }, [])
  const lazyChildren = useMemo(() => {
    return mounted ? (
      <div
        className="animate-fade-in flex-col h-full flex-1"
        style={{
          animationDuration: "300ms",
        }}
      >
        {children}
      </div>
    ) : null
  }, [children, mounted])

  return (
    <div
      className="rounded-2xl"
      style={{
        padding: !state.showBackground ? `${padding}px` : "0px",
      }}
    >
      <div
        ref={innerRef}
        className={twMerge(
          "rounded-2xl",
          state.showBackground ? background.className : ""
        )}
        style={{
          padding: state.showBackground ? `${padding}px` : "0px",
          ...(state.showBackground ? background.style : {}),
          ...dimensionsStyles,
        }}
      >
        <div className="flex flex-col h-full">
          <Card
            className="rounded-2xl h-full flex flex-col gap-2"
            style={{
              boxShadow: "rgba(0, 0, 0, 0.05) 0px 3px 8px",
            }}
          >
            {lazyChildren}
            {state.sourcesDisplay === "inside-card" && (
              <div className="flex flex-col gap-3 mt-4">
                {sourcesAndDefinitionsText && (
                  <div className="flex gap-3">
                    <Icon
                      as={Info}
                      width={12}
                      className="mt-[2px] text-fg-secondary flex-shrink-0"
                    />
                    <div
                      className={`text-xs break-words w-full opacity-70 text-fg-secondary`}
                    >
                      {sourcesAndDefinitionsText}
                    </div>
                  </div>
                )}
                {isChartFootnotesEnabled && footnoteText && (
                  <div className="flex gap-3">
                    <Icon
                      as={Note}
                      width={12}
                      className="mt-[2px] text-fg-secondary flex-shrink-0"
                    />
                    <div
                      className={`text-xs break-words w-full opacity-70 text-fg-secondary`}
                    >
                      {footnoteText}
                    </div>
                  </div>
                )}
              </div>
            )}
          </Card>
          {state.sourcesDisplay === "out-of-card" && (
            <div className="flex flex-col gap-3 mt-4">
              {sourcesAndDefinitionsText && (
                <div className="flex gap-3">
                  <Icon
                    as={Info}
                    width={12}
                    className={`mt-[2px] opacity-70 ${text.className}`}
                    style={text.style}
                  />
                  <div
                    className={`text-xs break-words w-full opacity-70 ${text.className}`}
                    style={text.style}
                  >
                    {sourcesAndDefinitionsText}
                  </div>
                </div>
              )}
              {isChartFootnotesEnabled && footnoteText && (
                <div className="flex gap-3">
                  <Icon
                    as={Note}
                    width={12}
                    className={`mt-[2px] opacity-70 ${text.className}`}
                    style={text.style}
                  />
                  <div
                    className={`text-xs break-words w-full opacity-70 ${text.className}`}
                    style={text.style}
                  >
                    {footnoteText}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
